import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Download, Paperclip } from '../../icons';
import { requestPresignedDownload } from '../../api/files';
import {
  formatBytes,
  isImageMime,
  isAudioMime,
  isVideoMime,
  isMessageAttachment,
  narrowAttachments,
} from '../../lib/attachments';
import { AttachmentAudio, AttachmentVideo } from './AttachmentMedia';
import { fileIconFor, fileIconClass, fileTypeLabel } from '../../lib/file-icon';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../lib/shared-i18n';
import { attachmentPreviewDoc } from '../../lib/preview-doc';
import { useChatPreviewStore } from '../../store/chatPreviewStore';
import { PreviewUnavailableModal } from '../../pages/ChatPage/ChatArea/PreviewUnavailableModal';
// chat-attachment-save (2026-08-26) — 첨부를 어디에 둘지 고르는 버튼(네 자리가 함께 쓴다).
import { AttachmentSaveMenu } from './AttachmentSaveMenu';
import type { MessageAttachment } from '../../types/api';

interface AttachmentListProps {
  /** ChatMessage.attachments — backend Json column. unknown 입력 → MessageAttachment[] narrow. */
  attachments: unknown;
  /**
   * chat-file-preview (2026-07-02) — 채팅 컨텍스트일 때 방 id. 존재하면 파일(비이미지) 카드가
   * **다운로드 버튼 + 본문 클릭**으로 동작한다: 본문 클릭 시 미리보기 가능 형식은 우측 미리보기
   * 패널, 아니면 '미리보기 불가' 경고 모달. mail/calendar 등 비채팅 사용처는 미전달 → 기존
   * 다운로드 버튼만 있는 동작 그대로 유지(무회귀).
   */
  roomId?: string;
  /**
   * 종류를 원래 MIME 대신 **짧은 이름**(DOCX·HWP·PDF)으로 보여준다 (2026-08-20).
   *
   * 원래 MIME 을 그대로 찍으면 OOXML 은 `application/vnd.openxmlformats-…document` 라 네 줄로
   * 접혀 칸을 무너뜨린다. 외부 메일을 가져오기 시작하자 첨부 칸이 이 글자로 깨졌다.
   * ★ 기본값은 끔 — 채팅·게시판 표시는 지금까지와 한 글자도 다르지 않다.
   */
  shortType?: boolean;
}

/**
 * AttachmentList (P2-17 후속) — message 의 첨부 배열을 시각화.
 *
 * 분기:
 *   - image/* → AttachmentImage (presigned GET URL 로 thumbnail)
 *   - audio/* → AttachmentAudio (인라인 오디오 플레이어) — chat-inline-media-playback 2026-07-12
 *   - video/* → AttachmentVideo (인라인 비디오 플레이어) — 동
 *   - 그 외   → AttachmentFile (icon + 이름 + 크기 + 다운로드 버튼)
 *   미디어 재생 실패(미지원 코덱 등)는 AttachmentFile 로 강등 → 다운로드 보존(무회귀).
 *
 * presigned GET 은 첫 사용자 액션 (image visible / 다운로드 클릭) 시 fetch.
 * 5분 TTL 만료 후 다시 사용하려면 component remount 또는 새 message — 현재는 단일 fetch.
 */
export function AttachmentList({ attachments, roomId, shortType }: AttachmentListProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const list = narrowAttachments(attachments);
  if (list.length === 0) return null;

  return (
    <ul className="chat-attachment-list" aria-label={tShared('shared.attach.list_aria')}>
      {list.map((a) => (
        <li key={a.fileId} className="chat-attachment-item" role="listitem">
          {renderAttachment(a, roomId, shortType)}
        </li>
      ))}
    </ul>
  );
}

// chat-inline-media-playback (2026-07-12) — MIME 별 분기(additive). audio/video 실패는
// AttachmentFile 로 강등(fallback prop) → 다운로드 경로 보존.
function renderAttachment(a: MessageAttachment, roomId?: string, shortType?: boolean) {
  const mime = a.mimeType ?? '';
  if (isImageMime(mime)) return <AttachmentImage attachment={a} roomId={roomId} />;
  if (isAudioMime(mime)) return <AttachmentAudio attachment={a} fallback={<AttachmentFile attachment={a} roomId={roomId} shortType={shortType} />} />;
  if (isVideoMime(mime)) return <AttachmentVideo attachment={a} fallback={<AttachmentFile attachment={a} roomId={roomId} shortType={shortType} />} />;
  return <AttachmentFile attachment={a} roomId={roomId} shortType={shortType} />;
}

function AttachmentImage({ attachment, roomId }: { attachment: MessageAttachment; roomId?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    requestPresignedDownload(attachment.fileId, { purpose: 'preview' }) // 이미지 임베드 = 「열람」(§3.4.1)
      .then((res) => { if (!cancelled) setUrl(res.downloadUrl); })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof Error ? err.message : 'preview_failed'); });
    return () => { cancelled = true; };
  }, [attachment.fileId]);

  if (error) {
    return <AttachmentFile attachment={attachment} error={error} roomId={roomId} />;
  }
  if (!url) {
    return (
      <div className="chat-attachment-image-placeholder" aria-busy="true" aria-label={tShared('shared.attach.image_loading')}>
        <Paperclip size={14} />
        <span className="chat-attachment-name">{attachment.name ?? tShared('shared.attach.image_fallback')}</span>
      </div>
    );
  }

  // 그림도 어디에 둘지 고를 수 있어야 한다 — 대화에서 가장 흔한 첨부인데 여기만 빠져 있었다.
  // 링크(새 탭 열기)는 그대로 두고, 저장 버튼을 **형제로** 겹쳐 놓는다
  // (<a> 안에 <button> 을 넣으면 파서가 바깥을 먼저 닫는다).
  const imageName = attachment.name ?? tShared('shared.attach.image_fallback');
  const downloadImage = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = imageName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <span className="chat-attachment-image-wrap">
      <a href={url} target="_blank" rel="noopener noreferrer" className="chat-attachment-image" aria-label={imageName}>
        <img src={url} alt={attachment.name ?? tShared('shared.attach.image_alt')} loading="lazy" />
      </a>
      <AttachmentSaveMenu
        roomId={roomId}
        fileId={attachment.fileId}
        onDownload={downloadImage}
        className="chat-attachment-image-save"
        buttonLabel={tShared('shared.attach.download_aria', { name: imageName })}
      >
        <Download size={14} />
      </AttachmentSaveMenu>
    </span>
  );
}

function AttachmentFile({ attachment, error, roomId, shortType }: {
  attachment: MessageAttachment; error?: string; roomId?: string;
  /**
   * 종류를 원래 MIME 대신 **짧은 이름**(DOCX·HWP·PDF)으로 (2026-08-20).
   * 기본값은 지금까지와 같다 — 켠 화면(메일)에서만 달라진다.
   */
  shortType?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(error ?? null);
  const [warnOpen, setWarnOpen] = useState(false);
  const openPreview = useChatPreviewStore((s) => s.openPreview);
  const FileIcon = fileIconFor({ name: attachment.name, mimeType: attachment.mimeType });
  const displayName = attachment.name ?? tShared('shared.attach.file_fallback');

  // chat-file-preview (2026-07-02): 파일 본문 클릭 → 미리보기 가능 형식이면 우측 패널, 아니면 경고 모달.
  const handlePreviewClick = useCallback(() => {
    if (!roomId) return;
    const doc = attachmentPreviewDoc(attachment);
    if (doc) openPreview(roomId, doc);
    else setWarnOpen(true);
  }, [attachment, roomId, openPreview]);

  const handleDownload = useCallback(async () => {
    setBusy(true);
    setLocalError(null);
    try {
      const res = await requestPresignedDownload(attachment.fileId);
      // anchor download — 브라우저가 직접 GET. CORS allow 필요 (MinIO 정책).
      const a = document.createElement('a');
      a.href = res.downloadUrl;
      a.download = res.name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : 'download_failed');
    } finally {
      setBusy(false);
    }
  }, [attachment.fileId]);

  const iconEl = (
    <div className={`chat-attachment-file-icon ${fileIconClass({ name: attachment.name, mimeType: attachment.mimeType })}`}>
      <FileIcon size={20} />
    </div>
  );
  const metaEl = (
    <div className="chat-attachment-file-meta">
      <div className="chat-attachment-name" title={attachment.name}>{displayName}</div>
      <div className="chat-attachment-sub">
        {shortType
          ? fileTypeLabel({ name: attachment.name, mimeType: attachment.mimeType })
          : (attachment.mimeType ?? 'unknown')} · {formatBytes(attachment.sizeBytes ?? 0)}
        {localError && <span className="chat-attachment-error"> · {localError}</span>}
      </div>
    </div>
  );

  const downloadButton = (
    <button
      type="button"
      className="chat-attachment-download-button"
      onClick={() => void handleDownload()}
      disabled={busy}
      aria-label={tShared('shared.attach.download_aria', { name: attachment.name ?? tShared('shared.attach.file_unknown_name') })}
      aria-busy={busy}
    >
      <Download size={14} />
    </button>
  );

  // chat-file-preview (2026-07-02): 채팅(roomId 존재) 에서는 다운로드 버튼 + 본문 클릭(미리보기/경고).
  // 비채팅(mail/calendar, roomId 없음) 은 기존 다운로드 버튼만 있는 카드 유지(무회귀).
  if (roomId) {
    return (
      <div className="chat-attachment-file chat-attachment-file--interactive" role="group" aria-label={displayName}>
        <button
          type="button"
          className="chat-attachment-file-main"
          onClick={handlePreviewClick}
          aria-label={tShared('shared.attach.preview_open_aria', { name: displayName })}
        >
          {iconEl}
          {metaEl}
        </button>
        <AttachmentSaveMenu
          roomId={roomId}
          fileId={attachment.fileId}
          onDownload={handleDownload}
          className="chat-attachment-download-button"
          buttonLabel={tShared('shared.attach.download_aria', { name: attachment.name ?? tShared('shared.attach.file_unknown_name') })}
        >
          <Download size={14} />
        </AttachmentSaveMenu>
        {warnOpen && (
          <PreviewUnavailableModal name={attachment.name ?? undefined} onClose={() => setWarnOpen(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="chat-attachment-file" role="group" aria-label={displayName}>
      {iconEl}
      {metaEl}
      {downloadButton}
    </div>
  );
}

// 2026-04-29 attachment-ssot-refactor: internal helpers (narrowAttachments / isMessageAttachment /
// isImageMime / formatBytes) 모두 lib/attachments.ts 로 이동. AttachmentList 는 *UI 만* 담당.
// __test__ backward compat — 테스트가 직접 import 하므로 lib 의 helper re-export.
export const __test__ = { narrowAttachments, isMessageAttachment, isImageMime };
